import React, { useState, useMemo } from 'react';
import { ChartConfig } from '../../api/aiDashboardApi';
import { TrendingUp, BarChart3, Activity, Zap, GitBranch, Layers, Dot } from 'lucide-react';

interface ModernChartRendererProps {
  config: ChartConfig;
}

export const ModernChartRenderer: React.FC<ModernChartRendererProps> = ({ config }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // Enhanced color palettes
  const colorPalettes = {
    modern: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'],
    gradient: ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe', '#43e97b', '#38f9d7'],
    professional: ['#1f2937', '#374151', '#6b7280', '#9ca3af', '#d1d5db', '#e5e7eb', '#f3f4f6', '#f9fafb'],
    vibrant: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#dda0dd', '#98d8c8', '#f7dc6f']
  };

  const colors = colorPalettes[config.colorScheme || 'modern'] || colorPalettes.modern;

  // Transform data for different chart types
  const transformedData = useMemo(() => {
    return config.data.map((item, index) => ({
      ...item,
      name: item.label || item.name || `Item ${index + 1}`,
      value: Number(item.value || item.count || 0),
      fill: colors[index % colors.length],
      color: colors[index % colors.length]
    }));
  }, [config.data, colors]);

  // Calculate statistics
  const stats = useMemo(() => {
    const values = transformedData.map(d => d.value);
    const total = values.reduce((sum, val) => sum + val, 0);
    const max = Math.max(...values);
    const min = Math.min(...values);
    const avg = total / values.length;
    
    return { total, max, min, avg, count: values.length };
  }, [transformedData]);

  // Streamgraph component
  const StreamgraphChart = () => {
    const width = 600;
    const height = 300;
    const margin = { top: 20, right: 30, bottom: 40, left: 50 };

    // Process data for streamgraph (simplified for now)
    // const processedData = config.data || [];

    return (
      <div className="w-full h-full flex items-center justify-center">
        <svg width={width} height={height} className="overflow-visible">
          <g transform={`translate(${margin.left},${margin.top})`}>
            {/* Streamgraph layers would be rendered here */}
            <text x={width/2 - margin.left} y={height/2} textAnchor="middle" className="text-gray-500">
              Streamgraph visualization
            </text>
            <text x={width/2 - margin.left} y={height/2 + 20} textAnchor="middle" className="text-sm text-gray-400">
              Multi-layered flow chart
            </text>
          </g>
        </svg>
      </div>
    );
  };

  // Horizon Chart component
  const HorizonChart = () => {
    return (
      <div className="w-full space-y-2">
        {transformedData.slice(0, 5).map((item, index) => (
          <div key={index} className="relative">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">{item.name}</span>
              <span className="text-sm text-gray-500">{item.value.toLocaleString()}</span>
            </div>
            <div className="relative h-12 bg-gray-100 rounded overflow-hidden">
              <div 
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-400 to-blue-600 transition-all duration-500"
                style={{ width: `${(item.value / stats.max) * 100}%` }}
              />
              <div className="absolute inset-0 flex items-center px-3">
                <div className="w-full h-1 bg-white/30 rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Connected Scatter Plot
  const ConnectedScatterPlot = () => {
    const width = 600;
    const height = 300;
    const margin = { top: 20, right: 30, bottom: 40, left: 50 };

    return (
      <div className="w-full h-full flex items-center justify-center">
        <svg width={width} height={height}>
          <g transform={`translate(${margin.left},${margin.top})`}>
            {transformedData.map((item, index) => {
              const x = (index / (transformedData.length - 1)) * (width - margin.left - margin.right);
              const y = ((stats.max - item.value) / stats.max) * (height - margin.top - margin.bottom);
              
              return (
                <g key={index}>
                  {index > 0 && (
                    <line
                      x1={(index - 1) / (transformedData.length - 1) * (width - margin.left - margin.right)}
                      y1={((stats.max - transformedData[index - 1].value) / stats.max) * (height - margin.top - margin.bottom)}
                      x2={x}
                      y2={y}
                      stroke={colors[0]}
                      strokeWidth={2}
                      className="opacity-70"
                    />
                  )}
                  <circle
                    cx={x}
                    cy={y}
                    r={6}
                    fill={item.color}
                    className="hover:r-8 transition-all cursor-pointer"
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseLeave={() => setActiveIndex(null)}
                  />
                  {activeIndex === index && (
                    <text x={x} y={y - 15} textAnchor="middle" className="text-xs fill-gray-700">
                      {item.value.toLocaleString()}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        </svg>
      </div>
    );
  };

  // Dot Plot
  const DotPlot = () => {
    const sortedData = [...transformedData].sort((a, b) => b.value - a.value);
    
    return (
      <div className="space-y-3">
        {sortedData.map((item, index) => (
          <div key={index} className="flex items-center gap-4">
            <div className="w-32 text-sm font-medium text-gray-700 truncate">
              {item.name}
            </div>
            <div className="flex-1 relative">
              <div className="h-8 bg-gray-100 rounded-full relative overflow-hidden">
                <div 
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all duration-700"
                  style={{ width: `${(item.value / stats.max) * 100}%` }}
                />
                <div 
                  className="absolute top-1/2 transform -translate-y-1/2 w-4 h-4 bg-white border-2 border-blue-600 rounded-full shadow-sm transition-all duration-300"
                  style={{ left: `${(item.value / stats.max) * 100}%`, marginLeft: '-8px' }}
                />
              </div>
            </div>
            <div className="w-20 text-sm text-gray-600 text-right">
              {item.value.toLocaleString()}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Slope Chart
  const SlopeChart = () => {
    const width = 600;
    const height = 300;
    const margin = { top: 40, right: 100, bottom: 40, left: 100 };

    return (
      <div className="w-full h-full flex items-center justify-center">
        <svg width={width} height={height}>
          <g transform={`translate(${margin.left},${margin.top})`}>
            {/* Left axis label */}
            <text x={-20} y={-10} textAnchor="middle" className="text-sm font-medium fill-gray-700">
              Previous
            </text>
            {/* Right axis label */}
            <text x={width - margin.left - margin.right + 20} y={-10} textAnchor="middle" className="text-sm font-medium fill-gray-700">
              Current
            </text>
            
            {transformedData.slice(0, 8).map((item: any, index) => {
              const y = (index / 7) * (height - margin.top - margin.bottom);
              const prevValue = item.previous || item.value * 0.8; // Mock previous value
              const currentValue = item.current || item.value;
              const isIncrease = currentValue > prevValue;
              
              return (
                <g key={index}>
                  {/* Slope line */}
                  <line
                    x1={0}
                    y1={y}
                    x2={width - margin.left - margin.right}
                    y2={y}
                    stroke={isIncrease ? '#10B981' : '#EF4444'}
                    strokeWidth={2}
                    className="opacity-70"
                  />
                  
                  {/* Left point */}
                  <circle cx={0} cy={y} r={4} fill={isIncrease ? '#10B981' : '#EF4444'} />
                  <text x={-10} y={y + 4} textAnchor="end" className="text-xs fill-gray-600">
                    {prevValue.toLocaleString()}
                  </text>
                  
                  {/* Right point */}
                  <circle cx={width - margin.left - margin.right} cy={y} r={4} fill={isIncrease ? '#10B981' : '#EF4444'} />
                  <text x={width - margin.left - margin.right + 10} y={y + 4} textAnchor="start" className="text-xs fill-gray-600">
                    {currentValue.toLocaleString()}
                  </text>
                  
                  {/* Label */}
                  <text x={(width - margin.left - margin.right) / 2} y={y - 8} textAnchor="middle" className="text-xs fill-gray-700">
                    {item.name}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>
      </div>
    );
  };

  // Treemap (simplified version)
  const TreemapChart = () => {
    const sortedData = [...transformedData].sort((a, b) => b.value - a.value);
    
    return (
      <div className="grid grid-cols-4 gap-2 h-full">
        {sortedData.slice(0, 8).map((item, index) => {
          const size = Math.max(1, Math.floor((item.value / stats.max) * 4));
          return (
            <div
              key={index}
              className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg p-3 text-white text-center hover:from-blue-500 hover:to-blue-700 transition-all duration-300 cursor-pointer flex flex-col justify-center"
              style={{
                minHeight: `${Math.max(60, (item.value / stats.max) * 120)}px`,
                backgroundColor: item.color,
                gridColumn: `span ${size}`
              }}
            >
              <div className="text-xs font-medium truncate">{item.name}</div>
              <div className="text-lg font-bold">{item.value.toLocaleString()}</div>
            </div>
          );
        })}
      </div>
    );
  };

  // Get chart icon
  const getChartIcon = () => {
    switch (config.type) {
      case 'streamgraph': return <Layers className="h-5 w-5" />;
      case 'horizon': return <Activity className="h-5 w-5" />;
      case 'connected-scatter': return <Dot className="h-5 w-5" />;
      case 'dot-plot': return <BarChart3 className="h-5 w-5" />;
      case 'slope-chart': return <TrendingUp className="h-5 w-5" />;
      case 'treemap': return <GitBranch className="h-5 w-5" />;
      case 'line': return <TrendingUp className="h-5 w-5" />;
      case 'area': return <Activity className="h-5 w-5" />;
      case 'scatter': return <Zap className="h-5 w-5" />;
      default: return <BarChart3 className="h-5 w-5" />;
    }
  };

  // Render appropriate chart
  const renderChart = () => {
    switch (config.type) {
      case 'streamgraph':
        return <StreamgraphChart />;
      case 'horizon':
        return <HorizonChart />;
      case 'connected-scatter':
        return <ConnectedScatterPlot />;
      case 'dot-plot':
        return <DotPlot />;
      case 'slope-chart':
        return <SlopeChart />;
      case 'treemap':
        return <TreemapChart />;
      default:
        return (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                {getChartIcon()}
              </div>
              <p className="font-medium">Chart type: {config.type}</p>
              <p className="text-sm">Advanced visualization</p>
            </div>
          </div>
        );
    }
  };

  // Generate sample data if no data is available
  if (!config.data || config.data.length === 0) {
    const sampleData = [
      { label: 'Sample A', value: 45, count: 45 },
      { label: 'Sample B', value: 32, count: 32 },
      { label: 'Sample C', value: 28, count: 28 },
      { label: 'Sample D', value: 15, count: 15 },
      { label: 'Sample E', value: 8, count: 8 }
    ];
    config = { ...config, data: sampleData };
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
              {getChartIcon()}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{config.title}</h3>
              <p className="text-sm text-gray-500 capitalize">{config.type.replace('-', ' ')} chart</p>
              {config.description && (
                <p className="text-xs text-gray-400 mt-1">{config.description}</p>
              )}
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="flex items-center gap-4 text-sm">
            <div className="text-center">
              <p className="text-gray-500">Total</p>
              <p className="font-semibold text-gray-800">{stats.total.toLocaleString()}</p>
            </div>
            <div className="text-center">
              <p className="text-gray-500">Avg</p>
              <p className="font-semibold text-gray-800">{Math.round(stats.avg).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-6">
        <div className="h-80">
          {renderChart()}
        </div>
      </div>

      {/* Insights */}
      {config.insights && config.insights.length > 0 && (
        <div className="px-6 pb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Key Insights</h4>
          <div className="space-y-1">
            {config.insights.slice(0, 3).map((insight, index) => (
              <p key={index} className="text-xs text-gray-600 flex items-center gap-2">
                <div className="w-1 h-1 bg-blue-500 rounded-full" />
                {insight}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Footer with detailed stats */}
      <div className="px-6 pb-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div className="text-center">
              <p className="text-gray-500 mb-1">Records</p>
              <p className="font-semibold text-gray-800">{stats.count}</p>
            </div>
            <div className="text-center">
              <p className="text-gray-500 mb-1">Maximum</p>
              <p className="font-semibold text-green-600">{stats.max.toLocaleString()}</p>
            </div>
            <div className="text-center">
              <p className="text-gray-500 mb-1">Minimum</p>
              <p className="font-semibold text-orange-600">{stats.min.toLocaleString()}</p>
            </div>
            <div className="text-center">
              <p className="text-gray-500 mb-1">Range</p>
              <p className="font-semibold text-purple-600">{(stats.max - stats.min).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};